import { restore, kStructuredCloneMarker, kSendMarker, kCloneMethod, Send } from '@cian/core/threads/index.js';
import { Some, Option } from '@cian/core/option/index.js';
import { asyncIterCollect, asyncIterMap, iteratorToAsync, iteratorFilterMap, iteratorFlatMap, iteratorMap, iteratorReduce, iteratorFlatten, Ordering, intoIterable, iteratorMaxBy, Ord } from '@cian/core/iterator/index.js';
import { defaultMatcher } from '../internal/filter/filter.js';
import '../internal/filter/comparator.js';
import { ContextualTransformer } from '../transformers/ContextualTransformer.js';
import { settingSortByPriority, Storage } from '../internal/source/storage.js';

const NAMESPACE = 'config.safe';
const SENDABLE_CONFIG_MARKER = NAMESPACE + '.SendableConfig';
class ConfigSafe {
    sources;
    matcher;
    transformers = [];
    constructor({ source, matcher, transformer }) {
        this.sources = Array.isArray(source) ? source : [source];
        this.matcher = matcher || defaultMatcher;
        if (transformer) {
            this.transformers = Array.isArray(transformer) ? transformer : [transformer];
        }
    }
    attach(context) {
        return new ContextualConfigSafe({
            context: { ...context },
            source: this.sources.slice(),
            matcher: this.matcher,
            transformer: this.transformers.slice(),
        });
    }
    get(key, context) {
        return this.find(key, context).map(setting => setting.value);
    }
    has(key, context) {
        return this.find(key, context).isSome();
    }
    dump(keys) {
        if (!keys) {
            return Array.from(iteratorFilterMap(this.sources, source => Some(Array.from(source.dump())))).flat();
        }
        const dumpKeys = Array.isArray(keys) ? keys : [keys];
        return Array.from(iteratorFlatMap(dumpKeys, key => iteratorFilterMap(this.sources, source => source.selectAll(key)))).flat();
    }
    dumpForContext(keys, context) {
        const dumpKeys = Array.isArray(keys) ? keys : [keys];
        return Option.collect(iteratorMap(dumpKeys, key => this.find(key, context)));
    }
    dumpByFilter(filter, context) {
        const settingFilter = setting => {
            if (!filter(setting)) {
                return false;
            }
            return this.matcher(setting, context);
        };
        const dump = iteratorReduce(iteratorFlatten(this.sources.map(source => source.dump()).values()), (acc, setting) => {
            if (settingFilter(setting) &&
                (acc[setting.key] === undefined ||
                    settingSortByPriority(acc[setting.key], setting) > Ordering.Equal)) {
                acc[setting.key] = setting;
            }
            return acc;
        }, {}).unwrap();
        return intoIterable(Object.values(dump));
    }
    find(key, context) {
        const settingFilter = setting => {
            return this.matcher(setting, context);
        };
        return iteratorMaxBy(iteratorFilterMap(this.sources, (source) => source.select(key, settingFilter)), (a, b) => Ord.numberToOrd(a.priority, b.priority), true);
    }
    settingTransform(setting) {
        if (!this.transformers.length || !setting) {
            return setting;
        }
        return this.transformers.reduce((result, transformer) => {
            return transformer.transform(result);
        }, setting);
    }
}
class SendableConfig extends ConfigSafe {
    static restore() {
        return {
            [kStructuredCloneMarker]: SENDABLE_CONFIG_MARKER,
            from(serialized) {
                const source = serialized.source.map(serializedSource => {
                    return restore(serializedSource).unwrap();
                });
                const matcher = restore(serialized.matcher).unwrap();
                return new SendableConfig({ source, matcher });
            },
        };
    }
    [kStructuredCloneMarker] = SENDABLE_CONFIG_MARKER;
    [kSendMarker] = 1;
    constructor({ source, matcher, transformer }) {
        super({ source, matcher, transformer });
    }
    async [kCloneMethod]() {
        const source = (await asyncIterCollect.toArray(asyncIterMap(iteratorToAsync(this.sources), s => (Send.isCloneable(s) ? s[kCloneMethod]() : s))));
        return new SendableConfig({ source, matcher: this.matcher, transformer: this.transformers });
    }
    toStructuredClone() {
        const { source, transferable } = this.sources.reduce((acc, s) => {
            const [marker, clone, transferable] = s.toStructuredClone();
            if (transferable) {
                acc.transferable.push(...transferable);
            }
            acc.source.push([marker, clone]);
            return acc;
        }, { source: [], transferable: [] });
        const matcher = this.matcher.toStructuredClone();
        return [this[kStructuredCloneMarker], { source, matcher }, transferable];
    }
}
class ContextualConfigSafe extends ConfigSafe {
    context;
    contextualSource;
    constructor(props) {
        super(props);
        const { context } = props;
        this.context = props.context;
        this.contextualSource = new Storage();
        this.sources.push(this.contextualSource);
        this.transformers.push(new ContextualTransformer(context));
    }
    attach(context) {
        return super.attach({ ...this.context, ...context });
    }
    get(key) {
        return super.get(key, this.context);
    }
    has(key) {
        return super.has(key, this.context);
    }
    dumpForContext(keys) {
        return super.dumpForContext(keys, this.context);
    }
    dumpByFilter(filter) {
        return super.dumpByFilter(filter, this.context);
    }
    injectSettings(settings) {
        return this.contextualSource.batchAdd(settings);
    }
}

export { ConfigSafe, ContextualConfigSafe, SENDABLE_CONFIG_MARKER, SendableConfig };
//# sourceMappingURL=safe.js.map
